import type { PaginationItem } from "./Pagination.types";

export interface PaginationItemsOptions {
  readonly pageCount: number;
  readonly page: number;
  /** Pages kept either side of the current page. */
  readonly siblingCount: number;
  /** Pages kept at each end of the list. */
  readonly boundaryCount: number;
}

/**
 * The page list to render: the boundary pages at each end, a window around the
 * current page, and ellipses standing in for whatever was skipped.
 *
 * The window keeps a fixed width, so the row of buttons never changes size as
 * the user pages through — a control that grew and shrank would slide the
 * chevrons out from under the cursor. When a gap turns out to hold a single
 * page, that page is rendered instead of an ellipsis: dots hiding one page
 * would cost an extra click to reach something that fits in the same space.
 */
export function buildPaginationItems({
  pageCount,
  page,
  siblingCount,
  boundaryCount,
}: PaginationItemsOptions): readonly PaginationItem[] {
  const boundaries = Math.max(0, Math.trunc(boundaryCount));
  const siblings = Math.max(0, Math.trunc(siblingCount));

  const startPages = range(1, Math.min(boundaries, pageCount));
  const endPages = range(Math.max(pageCount - boundaries + 1, boundaries + 1), pageCount);
  const firstEndPage = endPages[0];

  // The window is pinned away from both ends so it stays a constant width even
  // when the current page sits next to a boundary.
  const windowStart = Math.max(Math.min(page - siblings, pageCount - boundaries - siblings * 2 - 1), boundaries + 2);
  const windowEnd = Math.min(
    Math.max(page + siblings, boundaries + siblings * 2 + 2),
    firstEndPage === undefined ? pageCount - 1 : firstEndPage - 2,
  );

  const startGap: readonly PaginationItem[] =
    windowStart > boundaries + 2 ? [{ type: "ellipsis", side: "start" }]
    : boundaries + 1 < pageCount - boundaries ? [{ type: "page", page: boundaries + 1 }]
    : [];

  const endGap: readonly PaginationItem[] =
    windowEnd < pageCount - boundaries - 1 ? [{ type: "ellipsis", side: "end" }]
    : pageCount - boundaries > boundaries ? [{ type: "page", page: pageCount - boundaries }]
    : [];

  return [
    ...startPages.map(toPageItem),
    ...startGap,
    ...range(windowStart, windowEnd).map(toPageItem),
    ...endGap,
    ...endPages.map(toPageItem),
  ];
}

function toPageItem(page: number): PaginationItem {
  return { type: "page", page };
}

function range(start: number, end: number): readonly number[] {
  if (end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}
